import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, PackagePlus, Search } from "lucide-react";

export default function OpeningStock() {
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState({});
  const [qty, setQty] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, inv] = await Promise.all([api.get("/products"), api.get("/inventory")]);
      setProducts(pr.data);
      const map = {};
      (inv.data || []).forEach((m) => { map[m.product_id] = (map[m.product_id] || 0) + Number(m.signed_quantity || 0); });
      setStock(map);
    } catch (e) { console.error("Failed to load opening stock data", e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const items = Object.entries(qty)
      .filter(([, v]) => v !== "" && v !== undefined && v !== null)
      .map(([product_id, v]) => ({ product_id, quantity: Number(v) }));
    if (!items.length) { toast.error("Enter opening quantity for at least one product"); return; }
    setSaving(true);
    try {
      const r = await api.post("/inventory/opening-stock", { items });
      toast.success(`Opening stock set for ${r.data.updated} product(s)`);
      setQty({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save opening stock"); }
    setSaving(false);
  };

  const filtered = products.filter((p) => (p.product_name || "").toLowerCase().includes(q.toLowerCase()) || (p.sku || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="opening-stock-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head flex items-center gap-2"><PackagePlus className="w-6 h-6" /> Opening Stock</h1>
          <p className="text-sm text-slate-500 mt-0.5">Set starting inventory for all products at once. Overwrites any previous opening balance.</p>
        </div>
        <div className="sm:ml-auto flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="opening-stock-search" placeholder="Search product..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9 w-full sm:w-56 bg-white" />
          </div>
          <Button data-testid="opening-stock-save" onClick={save} disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-md gap-1.5 h-9 transition-all active:scale-95">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Opening Stock"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Product</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Unit</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Current Stock</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Opening Qty</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(6)].map((_, i) => (
              <tr key={i} className="border-b border-slate-100">{[...Array(4)].map((__, j) => <td key={j} className="px-4 py-3.5"><div className="ab-skeleton h-4" style={{ width: `${[60, 30, 40, 50][j]}%` }} /></td>)}</tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-16 text-center text-slate-400 text-sm">No products found</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`opening-row-${p.id}`}>
                <td className="px-4 py-3 text-slate-800 font-medium">{p.product_name}<div className="text-xs text-slate-400">{p.sku || ""}</div></td>
                <td className="px-4 py-3 text-slate-600">{p.unit || "-"}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-900">{(stock[p.id] || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right">
                  <Input data-testid={`opening-qty-${p.id}`} type="number" min="0" placeholder="0" value={qty[p.id] ?? ""} onChange={(e) => setQty((s) => ({ ...s, [p.id]: e.target.value }))} className="h-9 w-32 ml-auto text-right bg-white" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
